import { useCallback, useEffect, useState } from "react";

import displayToast from "@common/components/Toast/displayToast";
import { SdkInitializationsAnalyticsPayload, SdkInitializationsTimeData } from "@common/models/Analytics.model";
import { CreditCard, Customer, Invoice, Plan, PlanInterval, PlanType } from "@common/models/billing";
import { isAdmin } from "@common/models/Member.model";
import { useAppDeps } from "@main/App.dependencies";
import { useAppSelectionContext } from "@main/context/App/AppSelectionContext";
import { useUpdateSelectedWorkspace } from "@main/layouts/views/WorkspaceRootLayout";
import { useAnalyticsDeps } from "@main/pages/Release/Release";
import { useQuery } from "@tanstack/react-query";
import { format, subYears } from "date-fns";

import { useAdministrationDeps } from "../Administration";

export const useBillingViewConsumer = () => {
  const { selectedWorkspace } = useAppSelectionContext();

  const { administrationService } = useAdministrationDeps();
  const { appsService } = useAppDeps();
  const { analyticsService } = useAnalyticsDeps();

  const [customer, setCustomer] = useState<Customer>();
  const [upcomingAmount, setUpcomingAmount] = useState<number>();
  const [creditCard, setCreditCard] = useState<CreditCard>();
  const [receipts, setReceipts] = useState<Invoice[]>([]);
  const [globalLoading, setGlobalLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  const { updateSelectedWorkspace } = useUpdateSelectedWorkspace();

  const [plans, setPlans] = useState<Plan[]>([]);
  const [addOns, setAddOns] = useState<Plan[]>([]);

  const getBillingInfo = useCallback(async () => {
    if (!selectedWorkspace) return;
    setGlobalLoading(true);
    try {
      const { data: customer } = await appsService.getCustomerInfo(selectedWorkspace.id);
      setCustomer(customer);

      if (isAdmin(selectedWorkspace?.role)) {
        const { data } = await administrationService.getCreditCard(selectedWorkspace.id);
        setCreditCard(data);

        const invoices = await administrationService.getInvoices(selectedWorkspace.id);
        setReceipts(invoices.data);
      }
    } catch (error) {
      displayToast({
        title: "Something went wrong",
        content: "There was an error while fetching customer info",
      });
    } finally {
      setGlobalLoading(false);
    }
  }, [administrationService, appsService, selectedWorkspace]);

  const getSubscriptionPlans = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await administrationService.getSubscriptionPlans();
      const plans = data.filter((plan) => plan.type === PlanType.PLAN);

      const sortedPlans = plans
        .map((plan) => {
          if (plan.interval === PlanInterval.YEAR || plan.interval === PlanInterval.WEEK) {
            return { ...plan, sort_number: 0 };
          }
          return { ...plan, sort_number: 1 };
        })
        .sort((a, b) => a.sort_number - b.sort_number);

      setPlans(sortedPlans);

      const addOns = data.filter((addOn) => addOn.type === PlanType.DEVICES);
      setAddOns(addOns);
    } catch (error) {
      displayToast({
        title: "Something went wrong",
        content: "There was an error while fetching subscription plans",
      });
    } finally {
      setLoading(false);
    }
    () => {
      setLoading(false);
    };
  }, [administrationService]);

  const cancelSubscription = useCallback(async () => {
    if (!selectedWorkspace) return;

    setLoading(true);

    try {
      await administrationService.handleSubscriptionActivation(selectedWorkspace.id, false);
      displayToast({
        title: "Your wish was our command",
        content: "Your subscription will be cancelled at the end of your current billing period. Sorry to see you go.",
      });

      const { data } = await appsService.getCustomerInfo(selectedWorkspace.id);
      setCustomer(data);
    } catch (error) {
      displayToast({
        title: "Something went wrong",
        content: error?.response?.data?.message ?? "Please try again.",
      });
    } finally {
      setLoading(false);
    }
  }, [administrationService, selectedWorkspace, appsService]);

  const resumeSubscription = useCallback(async () => {
    if (!selectedWorkspace) return;

    setLoading(true);

    try {
      await administrationService.handleSubscriptionActivation(selectedWorkspace.id, true);
      displayToast({
        title: "Subscription resumed",
        content: "Thank you and welcome back.",
      });

      const { data } = await appsService.getCustomerInfo(selectedWorkspace.id);

      await updateSelectedWorkspace(selectedWorkspace);

      setCustomer(data);
    } catch (error) {
      displayToast({
        title: "Something went wrong",
        content: error?.response?.data?.message ?? "Please try again.",
      });
    } finally {
      setLoading(false);
    }
  }, [administrationService, selectedWorkspace, appsService, updateSelectedWorkspace]);

  const updateCreditCard = useCallback(async () => {
    if (!selectedWorkspace) return;

    setLoading(true);

    try {
      const res = await administrationService.updatePaymentMethod(selectedWorkspace.id);

      window.location.replace(res.data.url);
    } catch (error) {
      displayToast({
        title: "Something went wrong",
        content: error?.response?.data?.message ?? "Please try again.",
      });
    } finally {
    }
  }, [administrationService, selectedWorkspace]);

  const getUpcomingAmount = useCallback(async () => {
    if (!selectedWorkspace) return;

    setLoading(true);

    try {
      const res = await administrationService.getUpcomingAmount(selectedWorkspace.id);
      setUpcomingAmount(res.data.amount);
    } catch (error) {
      displayToast({
        title: "Something went wrong",
        content: error?.response?.data?.message ?? "Please try again.",
      });
    } finally {
      setLoading(false);
    }
  }, [administrationService, selectedWorkspace]);

  useEffect(() => {
    getBillingInfo();
    getSubscriptionPlans();
    getUpcomingAmount();
  }, [getBillingInfo, getSubscriptionPlans, getUpcomingAmount]);

  const getSdkInitializationsAnalytics = async (): Promise<SdkInitializationsTimeData[]> => {
    if (!selectedWorkspace) return [];

    const payload: SdkInitializationsAnalyticsPayload = {
      start_date: format(subYears(new Date(), 1), "yyyy-MM-dd"),
      end_date: format(new Date(), "yyyy-MM-dd"),
      group: "month",
    };
    const { data } = await analyticsService.getSdkInitializationsAnalytics(selectedWorkspace.id, payload);

    return data?.data ?? [];
  };

  const {
    data: sdkInitializationsData,
    isError: isSdkInitializationsError,
    isLoading: isSdkInitializationsLoading,
  } = useQuery<SdkInitializationsTimeData[]>(
    ["sdkInitializations", { selectedWorkspace }],
    getSdkInitializationsAnalytics,
    {
      refetchOnWindowFocus: false,
      cacheTime: Infinity,
    },
  );

  return {
    customer,
    upcomingAmount,
    globalLoading,
    loading,
    creditCard,
    updateCreditCard,
    receipts,
    plans,
    addOns,
    cancelSubscription,
    resumeSubscription,
    sdkInitializationsData,
    isSdkInitializationsError,
    isSdkInitializationsLoading,
  };
};
